import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FunctionComponent,
} from 'react';
import { useGraphViewPerformanceTracking } from 'tabview/graphComponent/ArdoqGraphComponent';
import { PersonalSetting, ViewIds } from '@ardoq/api-types';
import { getMainViewpointModeViewModel$ } from './modernizedViewModel$';
import type {
  ModernizedBlockDiagramCommands,
  ModernizedBlockDiagramViewModelProperties,
  ModernizedBlockDiagramViewStatus,
} from './types';
import { connect } from '@ardoq/rxbeach';
import { WithLoadingIndicator } from 'tabview/WithLoadingIndicator';
import {
  ModernizedBlockDiagramViewCore,
  type ModernizedBlockDiagramViewCoreHandle,
} from './ModernizedBlockDiagramViewCore';
import { toViewpointModeMainViewInstanceId } from 'appLayout/mainViewInstanceIds';
import { ViewLegendContainer } from '@ardoq/graph';
import { ViewAndLegendContainer } from 'tabview/ViewAndLegendContainer';
import { MainPaneLoader } from 'streams/views/mainContent/MainPaneLoader';
import { DataLimit } from 'components/DataLimit/DataLimit';
import { ErrorInfoBox } from '@ardoq/error-info-box';
import { ViewLegend } from '@ardoq/view-legend';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import { HeightOffset } from 'views/viewLegend/useViewLegendSubscription';
import { BlockDiagramVisualDiffSwitcher } from './view/BlockDiagramVisualDiffSwitcher';
import { usePersonalSettingToggle } from 'streams/currentUser/usePersonalSettingToggle';
import {
  ZoomControl,
  ZoomControls,
} from 'atomicComponents/Zoomable/ZoomControls';
import { ZoomContainer } from 'tabview/relationshipDiagrams/atoms';
import { IconName } from '@ardoq/icons';
import { t } from '@ardoq/i18n';
import { DisabledZoomControls, type ViewContext } from '@ardoq/view-settings';
import { getZoomControlsDisabledState } from './utils';
import { createModernizedBlockDiagramCommands } from './commands';

const VIEW_ID = ViewIds.MODERNIZED_BLOCK_DIAGRAM;
const MAIN_VIEWPOINT_MODE_VIEW_COMMANDS = createModernizedBlockDiagramCommands(
  toViewpointModeMainViewInstanceId(VIEW_ID)
);
const ZOOM_INCREMENT = 1.2;

export const WrappedModernizedBlockDiagramView = (
  props: ModernizedBlockDiagramViewModelProperties &
    ModernizedBlockDiagramViewStatus & {
      commands?: ModernizedBlockDiagramCommands;
    }
) => {
  useGraphViewPerformanceTracking(props, VIEW_ID);

  const containerRef = useRef<HTMLDivElement>(null);
  const coreRef = useRef<ModernizedBlockDiagramViewCoreHandle>(null);

  const {
    viewInstanceId,
    i18nLocale,
    isViewEmpty,
    isViewpointMode,
    scopeDiffState,
    uiPropsAndCallbacks: { legendProps },
    viewSettings,
    errors,
    hasClones,
    isPresentationMode,
    errorInfoBoxLabels,
    scenarioName,
    isAboveLimit,
    isEmpty,
    canBypass,
    isCanvasPrototype,
    hideZoomControls,
    // Core pass-through fields, forwarded to ModernizedBlockDiagramViewCore.
    groups,
    nodes,
    edges,
    clearEdges,
    performanceTrackingId,
    loadedScenarioData,
    commands = MAIN_VIEWPOINT_MODE_VIEW_COMMANDS,
    urlFieldValuesByComponentId,
    urlFieldValuesByReferenceId,
    expandedStateChangingGroupId,
    selectableItems,
    loadingSource,
    viewStatusReporter,
    dataWorkToken,
  } = props;

  const isScopeLoading = scopeDiffState.isLoading;
  const isLegendActive = viewSettings.isLegendActive;

  const [bypassLimit, setBypassLimit] = useState(false);
  const showWarning = isAboveLimit && !bypassLimit;

  const [clearedErrors, setClearedErrors] = useState(false);
  const [clearedHasClones, setClearedHasClones] = useState(false);
  const [neverShowAgain, toggleNeverShowAgain] = usePersonalSettingToggle(
    PersonalSetting.NEVER_SHOW_CLONE_INFO_AGAIN_MODERNIZED_BLOCK_DIAGRAM
  );

  const toggleLegend = useCallback(() => {
    commands.toggleLegend(!isLegendActive);
  }, [commands, isLegendActive]);

  const zoomIn = useCallback(() => coreRef.current?.zoomIn(), []);
  const zoomOut = useCallback(() => coreRef.current?.zoomOut(), []);
  const fitContent = useCallback(() => coreRef.current?.fitContent(), []);
  const optimizeLayout = useCallback(
    () => coreRef.current?.optimizeLayout() ?? Promise.resolve(),
    []
  );

  const fitWindowDisabledRef = useRef(false);

  const renderingParameters = useRef<ViewContext>({
    getContainer: () => containerRef.current,
    getGraphComponent: () => coreRef.current?.getGraphComponent() ?? null,
    optimizeLayout,
    zoomIn,
    zoomOut,
    toggleLegend,
    isLegendActive,
    zoomControls: null,
    zoomControlsDisabledState: DisabledZoomControls.NONE,
    viewInstanceId,
  });
  renderingParameters.current.toggleLegend = toggleLegend;
  renderingParameters.current.isLegendActive = isLegendActive;

  const updateZoomControlsDisabledState = useCallback(() => {
    const graphComponent = coreRef.current?.getGraphComponent();
    if (!graphComponent) {
      return;
    }
    renderingParameters.current.zoomControlsDisabledState =
      getZoomControlsDisabledState({
        graphComponent,
        zoomIncrement: ZOOM_INCREMENT,
        fitWindowDisabled: fitWindowDisabledRef.current,
      });
    commands.updateViewContext(renderingParameters.current);
  }, [commands]);

  useEffect(() => {
    commands.updateViewContext(renderingParameters.current);
  });

  useEffect(() => {
    updateZoomControlsDisabledState();
  }, [updateZoomControlsDisabledState]);

  const enableFitWindow = useCallback(() => {
    fitWindowDisabledRef.current = false;
    updateZoomControlsDisabledState();
  }, [updateZoomControlsDisabledState]);

  const disableFitWindow = useCallback(() => {
    fitWindowDisabledRef.current = true;
    updateZoomControlsDisabledState();
  }, [updateZoomControlsDisabledState]);

  const additionalZoomControls = useMemo(
    () => (
      <ZoomControl
        iconName={IconName.OPTIMIZE_LAYOUT}
        onClick={optimizeLayout}
        label={t(
          i18nLocale,
          'tabview:blockDiagram.view.optimizeLayout',
          'Optimize layout'
        )}
      />
    ),
    [optimizeLayout, i18nLocale]
  );

  return (
    <WithLoadingIndicator
      viewInstanceId={viewInstanceId}
      loadingSource={loadingSource}
      showContentWhileLoading
      i18nLocale={i18nLocale}
    >
      <div className={`tab-pane ${VIEW_ID}Tab active`}>
        <ViewAndLegendContainer ref={containerRef}>
          <MainPaneLoader isLoading={isScopeLoading} />
          <ModernizedBlockDiagramViewCore
            ref={coreRef}
            viewInstanceId={viewInstanceId}
            isViewpointMode={isViewpointMode}
            viewSettings={viewSettings}
            groups={groups}
            nodes={nodes}
            edges={edges}
            clearEdges={clearEdges}
            performanceTrackingId={performanceTrackingId}
            viewStatusReporter={viewStatusReporter}
            dataWorkToken={dataWorkToken}
            loadedScenarioData={loadedScenarioData}
            commands={commands}
            urlFieldValuesByComponentId={urlFieldValuesByComponentId}
            urlFieldValuesByReferenceId={urlFieldValuesByReferenceId}
            expandedStateChangingGroupId={expandedStateChangingGroupId}
            isPresentationMode={isPresentationMode}
            isAboveLimit={isAboveLimit}
            isEmpty={isEmpty}
            selectableItems={selectableItems}
            i18nLocale={i18nLocale}
            bypassLimit={bypassLimit}
            onWindowChanged={enableFitWindow}
            onWindowFitted={disableFitWindow}
          />
          {!isViewEmpty && (
            <>
              {showWarning ? (
                <DataLimit
                  isAboveLimit={isAboveLimit}
                  isEmpty={isEmpty}
                  canBypass={canBypass}
                  onBypassClick={() => setBypassLimit(true)}
                  viewId={VIEW_ID}
                  isViewpointMode={isViewpointMode}
                  isCanvasPrototype={isCanvasPrototype}
                />
              ) : (
                !hideZoomControls && (
                  <ZoomContainer>
                    <ZoomControls
                      zoomIn={zoomIn}
                      zoomOut={zoomOut}
                      zoomCenter={fitContent}
                      i18nLocale={i18nLocale}
                    >
                      {additionalZoomControls}
                    </ZoomControls>
                  </ZoomContainer>
                )
              )}
              <HeightOffset>
                {heightOffset => (
                  <ViewLegendContainer
                    heightOffset={heightOffset}
                    visible={isLegendActive}
                  >
                    <ViewLegend
                      {...legendProps}
                      labels={getViewLegendLabels(i18nLocale)}
                    />
                  </ViewLegendContainer>
                )}
              </HeightOffset>
              <ErrorInfoBox
                errors={clearedErrors ? [] : errors}
                hasClones={!neverShowAgain && !clearedHasClones && hasClones}
                clearHasClones={() => setClearedHasClones(true)}
                clearErrors={() => setClearedErrors(true)}
                isShowNeverAgainSet={neverShowAgain}
                toggleNeverShowAgain={toggleNeverShowAgain}
                isPresentationMode={isPresentationMode}
                {...errorInfoBoxLabels}
              />
              {(scopeDiffState.scopeDiff || isScopeLoading) && (
                <BlockDiagramVisualDiffSwitcher
                  viewId={VIEW_ID}
                  scenarioName={scenarioName}
                  activeDiffMode={viewSettings.activeDiffMode}
                  i18nLocale={i18nLocale}
                />
              )}
            </>
          )}
        </ViewAndLegendContainer>
      </div>
    </WithLoadingIndicator>
  );
};

export const ModernizedBlockDiagramView: FunctionComponent = connect(
  WrappedModernizedBlockDiagramView,
  getMainViewpointModeViewModel$()
);
